import { useMemo } from 'react';
import { useIsPagesLoading, usePage } from 'modules/page/pageSelector';
import { isUnauthorizedApiClientError } from 'api/client/isUnauthorizedApiClientError';
import { useAnalyticsHealthQuery } from 'app/modules/analytics/hooks/useAnalyticsHealthQuery';
import {
  getAnalyticsEligiblePageIds,
  isAnalyticsEligiblePage,
  omitIneligiblePagesFromProfileGroups,
} from 'app/modules/analytics/utils/analyticsEligibleProfileGroups';
import type { PageData, ProfileGroup } from 'types/Page';

export interface AnalyticsInventory {
  pages: PageData[];
  profileGroups: ProfileGroup[];
  isLoading: boolean;
}

/** L0 — the profiles and groups analytics may show and query. */
export function useAnalyticsInventory(): AnalyticsInventory {
  const { pages: allPages, profileGroups: allProfileGroups } = usePage();
  const isLoading = useIsPagesLoading();

  const pages = useMemo(
    () => allPages.filter(isAnalyticsEligiblePage),
    [allPages],
  );

  const profileGroups = useMemo(
    () =>
      omitIneligiblePagesFromProfileGroups(
        allProfileGroups,
        getAnalyticsEligiblePageIds(pages),
      ),
    [allProfileGroups, pages],
  );

  return { pages, profileGroups, isLoading };
}

/** Kept out of useAnalyticsInventory so its many readers do not subscribe to the health query. */
export function useAnalyticsSessionAuthError(): boolean {
  const { isUnauthorized: isProfilesUnauthorized } = usePage();
  const { isError, error } = useAnalyticsHealthQuery();

  return useMemo(
    () =>
      isProfilesUnauthorized ||
      (isError && isUnauthorizedApiClientError(error)),
    [isProfilesUnauthorized, isError, error],
  );
}
